/**
 * Comprehensive caching utility for AuraKart
 * Supports both memory cache (fast) and sessionStorage (persistent)
 */

interface CacheItem<T> {
  data: T;
  timestamp: number;
  expiry: number;
}

interface CacheOptions {
  ttl?: number; // Time to live in milliseconds
  useSessionStorage?: boolean;
  skipMemoryCache?: boolean;
}

class CacheManager {
  private memoryCache = new Map<string, CacheItem<any>>();
  private readonly defaultTTL = 5 * 60 * 1000; // 5 minutes

  /**
   * Generate cache key with optional parameters
   */
  private generateKey(key: string, params?: Record<string, any>): string {
    if (!params || Object.keys(params).length === 0) {
      return key;
    }
    const paramString = Object.entries(params)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([k, v]) => `${k}:${v}`)
      .join('|');
    return `${key}:${paramString}`;
  }

  /**
   * Check if cache item is expired
   */
  private isExpired(item: CacheItem<any>): boolean {
    return Date.now() > item.timestamp + item.expiry;
  }

  /**
   * Get from sessionStorage
   */
  private getFromSessionStorage<T>(key: string): CacheItem<T> | null {
    try {
      const item = sessionStorage.getItem(key);
      return item ? JSON.parse(item) : null;
    } catch {
      return null;
    }
  }

  /**
   * Save to sessionStorage
   */
  private saveToSessionStorage<T>(key: string, item: CacheItem<T>): void {
    try {
      sessionStorage.setItem(key, JSON.stringify(item));
    } catch {
      // SessionStorage might be full, ignore error
    }
  }

  /**
   * Get cached data
   */
  get<T>(
    key: string, 
    params?: Record<string, any>, 
    options: CacheOptions = {}
  ): T | null {
    const cacheKey = this.generateKey(key, params);
    const { useSessionStorage = true, skipMemoryCache = false } = options;

    // Try memory cache first (fastest)
    if (!skipMemoryCache) {
      const memoryItem = this.memoryCache.get(cacheKey);
      if (memoryItem && !this.isExpired(memoryItem)) {
        return memoryItem.data;
      }
    }

    // Try sessionStorage (persistent across page refreshes)
    if (useSessionStorage) {
      const sessionItem = this.getFromSessionStorage<T>(cacheKey);
      if (sessionItem && !this.isExpired(sessionItem)) {
        // Restore to memory cache for faster future access
        if (!skipMemoryCache) {
          this.memoryCache.set(cacheKey, sessionItem);
        }
        return sessionItem.data;
      }
    }

    return null;
  }

  /**
   * Set cached data
   */
  set<T>(
    key: string,
    data: T,
    params?: Record<string, any>,
    options: CacheOptions = {}
  ): void {
    const cacheKey = this.generateKey(key, params);
    const { 
      ttl = this.defaultTTL, 
      useSessionStorage = true, 
      skipMemoryCache = false 
    } = options;

    const cacheItem: CacheItem<T> = {
      data,
      timestamp: Date.now(),
      expiry: ttl,
    };

    // Save to memory cache
    if (!skipMemoryCache) {
      this.memoryCache.set(cacheKey, cacheItem);
    }

    // Save to sessionStorage
    if (useSessionStorage) {
      this.saveToSessionStorage(cacheKey, cacheItem);
    }
  }

  /**
   * Remove specific cache entry
   */
  remove(key: string, params?: Record<string, any>): void {
    const cacheKey = this.generateKey(key, params);
    
    // Remove from memory
    this.memoryCache.delete(cacheKey);
    
    // Remove from sessionStorage
    try {
      sessionStorage.removeItem(cacheKey);
    } catch {
      // Ignore errors
    }
  }

  /**
   * Clear all cache entries matching a pattern
   */
  clearPattern(pattern: string): void {
    // Clear memory cache
    for (const key of this.memoryCache.keys()) {
      if (key.startsWith(pattern)) {
        this.memoryCache.delete(key);
      }
    }

    // Clear sessionStorage
    try {
      for (let i = sessionStorage.length - 1; i >= 0; i--) {
        const key = sessionStorage.key(i);
        if (key && key.startsWith(pattern)) {
          sessionStorage.removeItem(key);
        }
      }
    } catch {
      // Ignore errors
    }
  }

  /**
   * Clear all cache
   */
  clearAll(): void {
    this.memoryCache.clear();
    try {
      sessionStorage.clear();
    } catch {
      // Ignore errors
    }
  }

  /**
   * Clean up expired entries
   */
  cleanup(): void {
    // Clean memory cache
    for (const [key, item] of this.memoryCache.entries()) {
      if (this.isExpired(item)) {
        this.memoryCache.delete(key);
      }
    }

    // Clean sessionStorage
    try {
      for (let i = sessionStorage.length - 1; i >= 0; i--) {
        const key = sessionStorage.key(i);
        if (key) {
          const item = this.getFromSessionStorage(key);
          if (item && this.isExpired(item)) {
            sessionStorage.removeItem(key);
          }
        }
      }
    } catch {
      // Ignore errors
    }
  }

  /**
   * Get cache statistics
   */
  getStats(): { memoryEntries: number; sessionStorageEntries: number } {
    let sessionStorageEntries = 0;
    try {
      sessionStorageEntries = sessionStorage.length;
    } catch {
      // Ignore errors
    }
    
    return {
      memoryEntries: this.memoryCache.size,
      sessionStorageEntries
    };
  }
}

// Create singleton instance
export const cache = new CacheManager();

// Cache key constants
export const CACHE_KEYS = {
  // Home page data (1 hour TTL)
  HOME_DATA: 'home_data', // Alias for backward compatibility
  HOME_BANNERS: 'home_banners',
  HOME_STATS: 'home_stats',
  TRENDING_PRODUCTS: 'trending_products',
  NEW_ARRIVALS: 'new_arrivals',
  FEATURED_PRODUCTS: 'featured_products',
  CATEGORY_HIGHLIGHTS: 'category_highlights',

  // Category pages (30 minutes TTL)
  CATEGORY_PRODUCTS: 'category_products',
  CATEGORY_META: 'category_meta',

  // Product detail page - STATIC DATA ONLY (2 hours TTL)
  PRODUCT_STATIC: 'product_static',
  SIZE_CHART: 'size_chart',
  SIMILAR_PRODUCTS: 'similar_products',

  // Reviews (10 minutes TTL)
  PRODUCT_REVIEWS: 'product_reviews',
  REVIEW_SUMMARY: 'review_summary',
} as const;

// Cache TTL constants (in milliseconds)
export const CACHE_TTL = {
  HOME_DATA: 60 * 60 * 1000, // 1 hour
  CATEGORY_DATA: 30 * 60 * 1000, // 30 minutes
  PRODUCT_STATIC: 2 * 60 * 60 * 1000, // 2 hours
  REVIEWS: 10 * 60 * 1000, // 10 minutes
  SIZE_CHART: 24 * 60 * 60 * 1000, // 24 hours (rarely changes)
} as const;

// Utility functions for specific cache operations

/**
 * Cache static product data (name, images, description, brand, category)
 */
export const cacheProductStatic = (productId: number, product: any) => {
  const staticData = {
    productId: product.productId,
    productName: product.productName,
    productDescription: product.productDescription,
    categoryName: product.categoryName,
    subCategoryName: product.subCategoryName,
    images: product.images,
    details: product.details,
    rating: product.rating, // Static rating is OK to cache
  };

  cache.set(
    CACHE_KEYS.PRODUCT_STATIC,
    staticData,
    { productId },
    { ttl: CACHE_TTL.PRODUCT_STATIC }
  );
};

/**
 * Get cached static product data
 */
export const getCachedProductStatic = (productId: number) => {
  return cache.get(CACHE_KEYS.PRODUCT_STATIC, { productId });
};

/**
 * Clear product cache when product is updated
 */
export const clearProductCache = (productId: number) => {
  cache.remove(CACHE_KEYS.PRODUCT_STATIC, { productId });
  cache.remove(CACHE_KEYS.SIMILAR_PRODUCTS, { productId });
  cache.remove(CACHE_KEYS.PRODUCT_REVIEWS, { productId });
  cache.remove(CACHE_KEYS.REVIEW_SUMMARY, { productId });
};

/**
 * Clear category cache when products are updated
 */
export const clearCategoryCache = (categoryName?: string) => {
  if (categoryName) {
    cache.clearPattern(`${CACHE_KEYS.CATEGORY_PRODUCTS}:categoryName:${categoryName}`);
  } else {
    cache.clearPattern(CACHE_KEYS.CATEGORY_PRODUCTS);
  }
};

/**
 * Clear home page cache
 */
export const clearHomeCache = () => {
  cache.remove(CACHE_KEYS.HOME_BANNERS);
  cache.remove(CACHE_KEYS.HOME_STATS);
  cache.remove(CACHE_KEYS.TRENDING_PRODUCTS);
  cache.remove(CACHE_KEYS.NEW_ARRIVALS);
  cache.remove(CACHE_KEYS.FEATURED_PRODUCTS);
  cache.remove(CACHE_KEYS.CATEGORY_HIGHLIGHTS);
};

// Auto cleanup every 10 minutes
if (typeof window !== 'undefined') {
  setInterval(() => {
    cache.cleanup();
  }, 10 * 60 * 1000);
}